import type { ReactNode } from 'react';
import { cn } from '../cn.js';
import { EmptyState } from './EmptyState.js';
import { Panel, type PanelAccent } from './Panel.js';
import { Skeleton } from './Skeleton.js';

/**
 * The subset of a TanStack Query result `AsyncPanel` needs to pick a state — deliberately not the
 * full query object, so a caller can pass any query-shaped value (including one it has already
 * narrowed) without an import-only dependency on a particular query library.
 */
export interface AsyncPanelQueryState {
  isPending: boolean;
  isError: boolean;
  error?: unknown;
}

export interface AsyncPanelProps {
  title: ReactNode;
  /** A small tag beside the title, e.g. "By Department". Renders in every state, same as `Panel`. */
  tag?: ReactNode;
  /** Trailing header content — a "View all" link, a status badge. */
  action?: ReactNode;
  accent?: PanelAccent;
  query: AsyncPanelQueryState;
  /** Tailwind height class for the loading skeleton, e.g. 'h-48'. */
  skeletonHeight?: string;
  /** True once `query` has resolved but the result has nothing to show — a separate flag rather
   *  than inferred from `children`, since "no rows" is a property of the caller's own data (an
   *  empty array, a suppressed cohort), not something this component can see. */
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Defaults to a generic "Could not load data" — pass the panel's own specific copy (e.g.
   *  "Could not load rating distribution") to match what the hand-rolled ternary said before
   *  migrating to this component. */
  errorTitle?: string;
  /**
   * Pre-formatted error copy. `packages/ui` can't import `apps/web`'s `apiErrorMessage` — it's an
   * app-local helper, not a DOM-only-but-app-agnostic one — so the caller formats `query.error`
   * (via `apiErrorMessage(query.error, '...')`) before passing the result in here. Falls back to a
   * generic message when omitted.
   */
  errorDescription?: string;
  children: ReactNode;
}

const DEFAULT_ERROR_TITLE = 'Could not load data';
const DEFAULT_ERROR_DESCRIPTION = 'Please try again.';
const DEFAULT_EMPTY_TITLE = 'No data available';
const DEFAULT_EMPTY_DESCRIPTION = 'Nothing matches the current filters.';

/**
 * Wraps `Panel` with the isPending → isError → empty → data ternary that was hand-copied across
 * ~8 Insights/Dashboard panels (`RatingDistributionPanel`, `CycleCompletionPanel`,
 * `ScoreTrendPanel`, `SkillGapsPanel`, `TalentGridPanel`, `GoalAnalyticsPanel`,
 * `TeamBreakdownPanel`, `ScoreTrajectoryPanel`) — consolidating the loading skeleton, the error
 * `EmptyState`, and the empty-result `EmptyState` into one place so the three non-data states
 * always render identically instead of drifting panel to panel. Migrate call sites incrementally;
 * this is additive, not a breaking change to any panel individually.
 */
export function AsyncPanel({
  title,
  tag,
  action,
  accent,
  query,
  skeletonHeight = 'h-48',
  isEmpty = false,
  emptyTitle = DEFAULT_EMPTY_TITLE,
  emptyDescription = DEFAULT_EMPTY_DESCRIPTION,
  errorTitle = DEFAULT_ERROR_TITLE,
  errorDescription = DEFAULT_ERROR_DESCRIPTION,
  children,
}: AsyncPanelProps) {
  return (
    <Panel title={title} tag={tag} action={action} accent={accent}>
      {query.isPending ? (
        <Skeleton className={cn('w-full', skeletonHeight)} />
      ) : query.isError ? (
        <EmptyState title={errorTitle} description={errorDescription} />
      ) : isEmpty ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        children
      )}
    </Panel>
  );
}
